import { PasskeyLoginButton } from '@hurdl/authentication/frontend';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Icon,
  Stack,
} from '@hurdl/design-system';
import { useNavigate } from '@tanstack/react-router';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { type ReactElement } from 'react';

import { Logo } from '../brand/logo.js';

/**
 * The Admin_Panel's Sole_Factor_Login (R9.3).
 *
 * A hardware security key is the whole proof here — the key **is** the only factor, no
 * password and no second step — which is the panel's own policy, applied at its own login
 * page rather than in the capability (the capability's assertion login accepts any
 * registered credential; the panel's screen offers only the key). The page composes the
 * capability's {@link PasskeyLoginButton}, which drives the WebAuthn assertion ceremony
 * through the capability's store and never through a `fetch` or an address this module
 * holds (panel invariant 2). On a verified assertion the session is held and the page
 * routes into the application.
 *
 * It renders **outside the application shell**: the Layout detects the login route and
 * renders this page alone, with no sidebar and no app bar, because a person who is not
 * signed in has no navigation to offer. The composition is tasteful rather than a stub —
 * a centred `Card` on the themed page, the wordmark above it, a line of reassurance, the
 * key icon, and the one control — and it respects the Design_System's tokens and both
 * Theme_Modes, declaring no colour, spacing or type of its own.
 */
export function LoginPage(): ReactElement {
  const navigate = useNavigate();

  function handleAuthenticated(): void {
    // Into the dashboard on success. What follows the sign-in is the application's
    // decision (R17.10); the panel's is to land the operator on the home it signed in to
    // reach.
    void navigate({ to: '/' });
  }

  return (
    // A centred column filling the themed page: the shell is absent here, so the page
    // owns its own vertical rhythm. `min-h-dvh` fills the viewport the ThemeSurface
    // painted; the content is a narrow column so the card does not stretch on a wide
    // screen.
    <div className="flex min-h-dvh w-full items-center justify-center p-hurdl-6">
      <Stack gap="large" align="centre" className="w-full max-w-sm">
        <Logo className="h-8 text-foreground" />

        <Card className="w-full">
          <CardHeader>
            <Stack gap="small" align="centre" className="text-center">
              <span className="text-hurdl-primary">
                <Icon icon={ShieldCheck} size="large" label="Secure sign-in" />
              </span>
              <CardTitle>Sign in to the Admin panel</CardTitle>
              <CardDescription>
                This panel is protected by a hardware security key. Insert your
                key and follow your browser’s prompt to continue.
              </CardDescription>
            </Stack>
          </CardHeader>

          <CardContent>
            <Stack gap="medium">
              <PasskeyLoginButton onAuthenticated={handleAuthenticated} />

              <Stack
                direction="horizontal"
                gap="small"
                align="centre"
                className="justify-center text-hurdl-muted-foreground"
              >
                <Icon icon={KeyRound} size="small" />
                <span className="text-hurdl-caption">
                  No password — your key is the only credential.
                </span>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </div>
  );
}
